import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type TaxObligationStatus = "PENDIENTE" | "EN_PREPARACION" | "EN_REVISION" | "PRESENTADO" | "PAGADO" | "VENCIDO";

export const TAX_STATUS_LABEL: Record<TaxObligationStatus, string> = {
  PENDIENTE: "Pendiente",
  EN_PREPARACION: "En preparación",
  EN_REVISION: "En revisión",
  PRESENTADO: "Presentado",
  PAGADO: "Pagado",
  VENCIDO: "Vencido",
};

export interface TaxObligationRecord {
  id: string;
  clientId: string;
  taxType: string;
  periodLabel: string;
  status: TaxObligationStatus;
  dueDate: string;
  filedDate: string | null;
  paidDate: string | null;
  amount: string | null;
  confirmationNumber: string | null;
  notes: string | null;
  createdAt: string;
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  assignedTo?: { id: string; fullName: string; email: string } | null;
}

export interface ListTaxObligationsFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: TaxObligationStatus;
  taxType?: string;
  search?: string;
  overdue?: boolean;
}

export function useTaxObligations(filters: ListTaxObligationsFilters = {}) {
  return useQuery({
    queryKey: ["taxes", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<TaxObligationRecord[]>>("/taxes", { params: filters });
      return data;
    },
  });
}

export interface CreateTaxObligationPayload {
  clientId: string;
  taxType: string;
  periodLabel: string;
  dueDate: string;
  status?: TaxObligationStatus;
  filedDate?: string;
  paidDate?: string;
  amount?: number | string;
  confirmationNumber?: string;
  notes?: string;
  assignedToUserId?: string;
}

export function useCreateTaxObligation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTaxObligationPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<TaxObligationRecord>>("/taxes", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["taxes"] }),
  });
}

export function useUpdateTaxObligation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateTaxObligationPayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<TaxObligationRecord>>(`/taxes/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["taxes"] }),
  });
}

export function useDeleteTaxObligation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/taxes/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["taxes"] }),
  });
}

export function taxObligationClientName(taxObligation: TaxObligationRecord): string | null {
  if (!taxObligation.client) return null;
  return taxObligation.client.clientType === "PERSONA_JURIDICA"
    ? taxObligation.client.businessName || taxObligation.client.tradeName || "Cliente"
    : taxObligation.client.fullName || "Cliente";
}

export function isTaxOverdue(taxObligation: TaxObligationRecord): boolean {
  if (!taxObligation.dueDate) return false;
  if (taxObligation.status === "PAGADO") return false;
  return new Date(taxObligation.dueDate) < new Date(new Date().toDateString());
}
